/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import { useLocation } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

import SearchPageHeader from 'bundles/composable-page-header-consumer/header-renderers/SearchPageHeader';
import ConsumerPageHeaderBffProvider from 'bundles/composable-page-header-consumer/providers/ConsumerPageHeaderBffProvider';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import withFetchLazyImages from 'bundles/page/components/shared/utils/withFetchLazyImages';
import {
  CoachColumnLayoutProvider,
  useCoachColumnLayout,
} from 'bundles/search-common/components/experiments/coachInSearch/CoachColumnLayoutContext';
import { COACH_PANEL_WIDTH_CSS } from 'bundles/search-common/components/experiments/coachInSearch/constants';
import { SearchExplainProvider } from 'bundles/search-common/components/internal-search-explainer/SearchExplainContext';
import useSearchExplainMetrics from 'bundles/search-common/components/internal-search-explainer/hooks/useSearchExplainMetrics';
import useSearchExplainState from 'bundles/search-common/components/internal-search-explainer/hooks/useSearchExplainState';
import { TakeoverPilotExperimentProvider } from 'bundles/search-common/experiments/takeoverPilot';
import { useSearchConfigs } from 'bundles/search-common/hooks/useSearchConfigs';
import { LearnerProvider } from 'bundles/search-common/providers/LearnerProvider';
import RouterProvider from 'bundles/search-common/providers/RouterProvider';
import SearchCoachProvider from 'bundles/search-common/providers/SearchCoachProvider';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { SearchRefreshProvider } from 'bundles/search-common/providers/SearchRefreshContext';
import TwoPanelSearchExperimentProvider from 'bundles/search-common/providers/TwoPanelSearchExperimentProvider';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { getAutoApplyFilters } from 'bundles/search-common/utils/experimentUtils';
import inServerContext from 'bundles/ssr/util/inServerContext';
import withMarketingConsent from 'bundles/user-consent/components/withMarketingConsent';

import 'css!bundles/search-common/__styles__/index';

type Props = {
  children: React.ReactNode;
};

const styles = {
  // Reserve a strip for the out-of-flow (fixed) coach panel so the full-bleed footer
  // doesn't slide under it. Width published as --coach-footer-carve-right; 0 = no-op.
  footerCarve: css`
    ${breakpoints.up('md')} {
      margin-right: var(--coach-footer-carve-right, 0);
    }
  `,
  // Active only when carved: bg matches the footer so the gutter reads as part of it.
  footerCarveActive: css`
    ${breakpoints.up('md')} {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }
  `,
};

const SearchPageFooter = () => {
  const { footerCarveRightPx, hideFooter } = useCoachColumnLayout();
  if (hideFooter) return null;
  return (
    <div
      css={[styles.footerCarve, footerCarveRightPx > 0 && styles.footerCarveActive]}
      // Carve must match the panel's responsive rendered width (COACH_PANEL_WIDTH_CSS),
      // not the raw px signal from context.
      style={
        { '--coach-footer-carve-right': footerCarveRightPx > 0 ? COACH_PANEL_WIDTH_CSS : '0px' } as React.CSSProperties
      }
    >
      <PageFooter />
    </div>
  );
};

const SearchApp = ({ children }: Props) => {
  const { ownsCourseraPlus, ownsCourseraLite } = useCourseraPlusProductOwnerships();
  const location = useLocation();
  const query = inferQueryFromParams(location?.query?.query) || '';
  const searchConfigs = useSearchConfigs(true, query);
  const shouldDoAutoFiltersLogic = getAutoApplyFilters();

  // Search explain state and metrics
  const { hasPermission, enableSearchExplain, toggleSearchExplain } = useSearchExplainState();
  const {
    metrics: queryMetrics,
    loading: metricsLoading,
    error: metricsError,
  } = useSearchExplainMetrics(query, enableSearchExplain);
  const searchExplainContextValue = React.useMemo(
    () => ({
      hasPermission,
      enableSearchExplain,
      toggleSearchExplain,
      queryMetrics,
      metricsLoading,
      metricsError: metricsError || undefined,
    }),
    [hasPermission, enableSearchExplain, toggleSearchExplain, queryMetrics, metricsLoading, metricsError]
  );

  // used to prevent browser from scrolling to the last scroll position when navigating back/refreshing.
  // needed due to infinite scroll implementation not handling scroll restoration. fixable with virtual scroll.
  if (!inServerContext && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  return (
    <div className="rc-SearchApp">
      <UniversalPageFeatures />
      <CoachColumnLayoutProvider>
        <RouterProvider>
          <TwoPanelSearchExperimentProvider>
            {/* TAKEOVER_PILOT: remove this provider to take the pilot down. */}
            <TakeoverPilotExperimentProvider>
              <SearchCoachProvider>
                <SearchRefreshProvider>
                  <SearchPageHeader />
                  <ConsumerPageHeaderBffProvider>
                    <SearchProvider searchConfigs={searchConfigs} shouldDoAutoFiltersLogic={shouldDoAutoFiltersLogic}>
                      <SearchExplainProvider value={searchExplainContextValue}>
                        <LearnerProvider ownsCourseraPlus={ownsCourseraPlus} ownsCourseraLite={ownsCourseraLite}>
                          {children}
                        </LearnerProvider>
                      </SearchExplainProvider>
                    </SearchProvider>
                  </ConsumerPageHeaderBffProvider>
                </SearchRefreshProvider>
              </SearchCoachProvider>
            </TakeoverPilotExperimentProvider>
          </TwoPanelSearchExperimentProvider>
        </RouterProvider>
        <SearchPageFooter />
      </CoachColumnLayoutProvider>
    </div>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'search',
      page: 'search',
    },
  })),
  withFetchLazyImages,
  withMarketingConsent
)(SearchApp);
